import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import type { ComponentProps, ReactNode } from "react";

export type DialogProps = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  trigger?: ReactNode;
  className?: string;
};

/** Above `.toast` (40) and `.dev-debug-drawer` (45); below `.pwa-splash` (100). */
export const dialogBackdropClass = "dialog-backdrop fixed inset-0 z-50";

export const dialogPopupClass =
  "fixed left-1/2 top-1/2 z-[51] w-[min(28rem,calc(100vw-2rem-var(--safe-left,0px)-var(--safe-right,0px)))] max-w-[calc(100vw-2rem-var(--safe-left,0px)-var(--safe-right,0px))] -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-panel)] border border-hairline bg-panel text-ink shadow-none outline-none";

const panelClass = dialogPopupClass;

/**
 * Thin Base UI Dialog wrapper with instrument-panel chrome.
 * Compound parts are also re-exported for advanced composition.
 */
export function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  title,
  description,
  children,
  trigger,
  className,
}: DialogProps) {
  return (
    <BaseDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <BaseDialog.Trigger render={<span />}>{trigger}</BaseDialog.Trigger> : null}
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className={dialogBackdropClass} />
        <BaseDialog.Popup className={[panelClass, "p-4", className].filter(Boolean).join(" ")}>
          <BaseDialog.Title className="text-sm font-medium tracking-wide text-ink">
            {title}
          </BaseDialog.Title>
          {description ? (
            <BaseDialog.Description className="mt-1 text-xs text-ink-muted">
              {description}
            </BaseDialog.Description>
          ) : null}
          <div className="mt-3">{children}</div>
          <BaseDialog.Close className="mt-4 rounded-[var(--radius-control)] border border-hairline bg-panel-hi px-3 py-1.5 text-xs text-ink hover:border-signal">
            Close
          </BaseDialog.Close>
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

export const DialogRoot = BaseDialog.Root;
export const DialogTrigger = BaseDialog.Trigger;
export const DialogPortal = BaseDialog.Portal;
export const DialogBackdrop = BaseDialog.Backdrop;
export const DialogPopup = BaseDialog.Popup;
export const DialogTitle = BaseDialog.Title;
export const DialogDescription = BaseDialog.Description;
export const DialogClose = BaseDialog.Close;

export type DialogRootProps = ComponentProps<typeof BaseDialog.Root>;
